import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AnimatedSection, AnimatedText, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryToPage = {
  corporate: '/services/corporate',
  wedding: '/services/wedding',
  launch: '/services/launch',
  private: '/services/private',
};

export function ServicePage({
  categoryKey,
  heroImage,
  overline,
  headline,
  headlineAccent,
  intro,
  inclusions,
  processSteps,
  signatureDetails,
  faqItems,
}) {
  const [relatedPortfolio, setRelatedPortfolio] = useState([]);
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, -80]);

  useEffect(() => {
    axios.get(`${API}/portfolio?category=${categoryKey}`)
      .then(res => setRelatedPortfolio(res.data.slice(0, 3)))
      .catch(() => {});
  }, [categoryKey]);

  return (
    <div style={{ backgroundColor: '#070708' }}>
      {/* Hero */}
      <section
        data-testid="service-hero"
        className="relative flex items-end overflow-hidden"
        style={{ minHeight: '70vh', paddingBottom: '5rem', paddingTop: '8rem' }}
        ref={heroRef}
      >
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img
            src={heroImage}
            alt={headline}
            className="w-full h-full object-cover"
            style={{ transform: 'scale(1.08)' }}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(7,7,8,0.9) 0%, rgba(7,7,8,0.65) 60%, rgba(7,7,8,0.35) 100%)' }} />
        </motion.div>

        <div className="relative z-10 container-luxury">
          <motion.span
            className="overline mb-5 block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            {overline}
          </motion.span>
          <motion.h1
            className="display-xl max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.85, ease: [0.22,1,0.36,1] }}
          >
            {headline}<br />
            {headlineAccent && <em style={{ color: '#C9A84C', fontStyle: 'italic' }}>{headlineAccent}</em>}
          </motion.h1>
        </div>
      </section>

      {/* Intro */}
      <section className="section-py" style={{ backgroundColor: '#0B0B0D' }}>
        <div className="container-luxury">
          <div className="max-w-2xl">
            <AnimatedText>
              <p className="font-display text-xl md:text-2xl font-light text-[rgba(245,240,232,0.85)] leading-relaxed">
                {intro}
              </p>
            </AnimatedText>
          </div>
        </div>
      </section>

      {/* Inclusions */}
      <section data-testid="service-inclusions" className="section-py" style={{ backgroundColor: '#070708' }}>
        <div className="container-luxury">
          <AnimatedText className="mb-12">
            <span className="overline mb-4 block">Kapsam</span>
            <h2 className="display-lg">Neler Dahil?</h2>
          </AnimatedText>

          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {inclusions.map((item, i) => (
              <StaggerItem key={i}>
                <div
                  className="flex items-start gap-4 p-6"
                  style={{ backgroundColor: '#0E0F12', border: '1px solid rgba(201,168,76,0.06)' }}
                >
                  <CheckCircle size={16} className="text-[#C9A84C] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-body text-sm font-medium text-[#F5F0E8] mb-1">{item.title}</h4>
                    {item.desc && (
                      <p className="font-body text-xs text-[rgba(245,240,232,0.55)] leading-relaxed">{item.desc}</p>
                    )}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Process */}
      <section data-testid="service-process" className="section-py" style={{ backgroundColor: '#0B0B0D' }}>
        <div className="container-luxury">
          <AnimatedText className="mb-14">
            <span className="overline mb-4 block">Süreç</span>
            <h2 className="display-lg">Adım Adım</h2>
          </AnimatedText>

          <div className="flex flex-col gap-0">
            {processSteps.map((step, i) => (
              <AnimatedSection key={step.num} delay={i * 0.08}>
                <div
                  className="flex items-start gap-8 py-8 group"
                  style={{ borderBottom: '1px solid rgba(201,168,76,0.08)' }}
                >
                  <span className="font-display text-4xl font-light text-[#C9A84C] opacity-35 flex-shrink-0 w-14 text-right">
                    {step.num}
                  </span>
                  <div className="flex-1">
                    <h4 className="font-display text-2xl font-medium text-[#F5F0E8] mb-2 group-hover:text-[#C9A84C] transition-colors duration-300">
                      {step.title}
                    </h4>
                    <p className="body-text">{step.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Signature Details */}
      {signatureDetails && (
        <section className="section-py" style={{ backgroundColor: '#070708' }}>
          <div className="container-luxury">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <AnimatedText>
                <span className="overline mb-5 block">Ayırt Edici Detaylar</span>
                <h2 className="display-lg mb-8">{signatureDetails.title}</h2>
                <p className="body-text mb-8">{signatureDetails.body}</p>
                <ul className="flex flex-col gap-3">
                  {signatureDetails.list.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 font-body text-sm text-[rgba(245,240,232,0.75)]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </AnimatedText>
              <AnimatedSection delay={0.2}>
                <div
                  className="relative overflow-hidden img-hover-zoom"
                  style={{ height: '400px' }}
                >
                  <img
                    src={signatureDetails.image}
                    alt={signatureDetails.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>
      )}

      {/* Related Portfolio */}
      {relatedPortfolio.length > 0 && (
        <section className="section-py" style={{ backgroundColor: '#0B0B0D' }}>
          <div className="container-luxury">
            <AnimatedText className="mb-12">
              <span className="overline mb-4 block">Portföy</span>
              <h2 className="display-lg">Bu Kategoriden Çalışmalar</h2>
            </AnimatedText>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedPortfolio.map((item, i) => (
                <AnimatedSection key={item.id} delay={i * 0.1}>
                  <div
                    className="relative overflow-hidden img-hover-zoom group"
                    style={{ height: '260px', border: '1px solid rgba(201,168,76,0.06)' }}
                  >
                    <img src={`${item.image_url}&w=600`} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" loading="lazy" />
                    <div className="absolute inset-0 card-overlay" />
                    <div className="absolute inset-0 p-5 flex flex-col justify-end">
                      <h4 className="font-display text-lg text-[#F5F0E8]">{item.title}</h4>
                      <p className="font-body text-xs text-[rgba(245,240,232,0.5)] mt-1">{item.location} · {item.year}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link to="/portfolio" className="btn-outline-gold text-xs tracking-[0.12em] uppercase inline-block">
                Tüm Portföy
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section data-testid="service-cta" className="section-py" style={{ backgroundColor: '#070708' }}>
        <div className="container-luxury">
          <div
            className="p-10 md:p-16 relative overflow-hidden"
            style={{ border: '1px solid rgba(201,168,76,0.12)' }}
          >
            <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 800px 400px at 50% 50%, rgba(201,168,76,0.05), transparent 70%)' }} />
            <div className="relative z-10 max-w-xl">
              <span className="overline mb-5 block">Projenizi Konuşalım</span>
              <h2 className="display-lg mb-6">Hazır Mısınız?</h2>
              <p className="body-text mb-8">
                Etkinliğinizi birlikte planlayalım. Size özel bir teklif hazırlamak için hemen başvurun.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/quote" className="btn-gold text-xs tracking-[0.12em] uppercase">
                  Teklif Al
                </Link>
                <a
                  href={`https://wa.me/905314037309?text=${encodeURIComponent('Merhaba, etkinlik hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline-gold text-xs tracking-[0.12em] uppercase"
                >
                  WhatsApp'tan Yaz
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqItems && faqItems.length > 0 && (
        <section className="section-py" style={{ backgroundColor: '#0B0B0D', paddingTop: '3rem' }}>
          <div className="container-luxury">
            <AnimatedText className="mb-10">
              <span className="overline mb-4 block">Sıkça Sorulanlar</span>
              <h2 className="display-md">Bu Hizmet Hakkında</h2>
            </AnimatedText>
            <div className="max-w-2xl">
              <Accordion type="single" collapsible className="space-y-2">
                {faqItems.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    style={{ borderBottom: '1px solid rgba(201,168,76,0.12)' }}
                    className="rounded-none px-0"
                  >
                    <AccordionTrigger className="font-body text-sm font-medium text-[#F5F0E8] hover:text-[#C9A84C] py-5 hover:no-underline text-left">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="font-body text-sm text-[rgba(245,240,232,0.65)] pb-5 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
